import { type UUID, assumeExhaustion } from '../../../shared/lib/typeHelpers.no-deps'
import type { FileUploadData } from './FileUploadData'
import type { FileExtension } from '../../../shared/domain/files/fileExtensions.no-deps'
import type { ReportClientId } from '../../../shared/domain/reports/types.no-deps'
import type { FileId } from '../../../shared/domain/files/types.no-deps'
import type { NoteId } from '../../../shared/domain/notes/types.no-deps'
import { uniq, map } from '../../../shared/lib/functional/array.no-deps'
import { pipe } from '../../../shared/lib/functional/pipe.no-deps'

export type Upload =
    | UploadInvalid
    | UploadWritingFileToDisk
    | UploadWriteToDiskFailed
    | UploadPendingSendFileMeta
    | UploadSendingFileMeta
    | UploadSendFileMetaFailed
    | UploadPendingRetryUploadFile
    | UploadUploadingFile
    | UploadFailed
    | Uploaded
    | UploadCancelled

export type UploadStatus =
    | 'UploadInvalid'
    | 'UploadWritingFileToDisk'
    | 'UploadWriteToDiskFailed'
    | 'UploadPendingSendFileMeta'
    | 'UploadSendingFileMeta'
    | 'UploadSendFileMetaFailed'
    | 'UploadPendingRetryUploadFile'
    | 'UploadUploadingFile'
    | 'UploadFailed'
    | 'Uploaded'
    | 'UploadCancelled'

export const uploadStatuses: UploadStatus[] = [
    'UploadInvalid',
    'UploadWritingFileToDisk',
    'UploadWriteToDiskFailed',
    'UploadPendingSendFileMeta',
    'UploadSendingFileMeta',
    'UploadSendFileMetaFailed',
    'UploadPendingRetryUploadFile',
    'UploadUploadingFile',
    'UploadFailed',
    'Uploaded',
    'UploadCancelled',
]

export type InvalidUploadReason =
    | {
          type: 'FileTooBig'
          limit: number
      }
    | {
          type: 'InvalidExtension'
          validExtensions: FileExtension[]
      }

/**
 * This will occur if the extension or file size is invalid
 */
export type UploadInvalid = UploadBase<'UploadInvalid'> & { reason: InvalidUploadReason }
/**
 * This should be the intial state for offline uploads
 */
export type UploadWritingFileToDisk = UploadBase<'UploadWritingFileToDisk'>
/**
 * This will occur if writing the file to disk fails (only relevant for offline downloads)
 */
export type UploadWriteToDiskFailed = UploadBase<'UploadWriteToDiskFailed'>

/**
 * When upload is created it is in this state.
 * Also if sending the file meta fails it will return to this state as many times as are specified.
 */
export type UploadPendingSendFileMeta = UploadBase<'UploadPendingSendFileMeta'> & FailCount
/**
 * It will be in this state while the file meta is saving
 */
export type UploadSendingFileMeta = UploadBase<'UploadSendingFileMeta'>
/**
 * Will only go to this state once the max retries is reached
 */
export type UploadSendFileMetaFailed = UploadBase<'UploadSendFileMetaFailed'>

/**
 * It will go to this state if uploading the file fails
 */
export type UploadPendingRetryUploadFile = UploadBase<'UploadPendingRetryUploadFile'> & FailCount
/**
 * It will go this state while the file is uploading
 */
export type UploadUploadingFile = UploadBase<'UploadUploadingFile'> & { bytesUploaded: number }
/**
 * It will go this once the max retires are hit
 */
export type UploadFailed = UploadBase<'UploadFailed'>
/**
 * It goes to this state once the upload is complete
 */
export type Uploaded = UploadBase<'Uploaded'>

/**
 * When an upload is cancelled by the user
 */
export type UploadCancelled = UploadBase<'UploadCancelled'>

export type ProcessableUpload = UploadPendingSendFileMeta | UploadPendingRetryUploadFile

export type UploadGroupId = ReportClientId | NoteId | UUID // Alias for extensibility

// Type Helpers

type UploadBase<S> = {
    status: S
    fileUploadData: FileUploadData
    groupId?: UploadGroupId
    customer?: number
    canBeDeletedWhenDone: boolean
}
type FailCount = { failCount: number; nextRetry?: Date }

//
// Helpers

export function getFileId(upload: Upload) {
    return upload.fileUploadData.fileId
}

export function isWritingFilesToDisk(upload: Upload): upload is UploadWritingFileToDisk {
    return upload.status === 'UploadWritingFileToDisk'
}

export function isUploadingFile(upload: Upload): upload is UploadUploadingFile {
    return upload.status === 'UploadUploadingFile'
}

export function isSendingFileMeta(upload: Upload): upload is UploadUploadingFile {
    return upload.status === 'UploadUploadingFile'
}

export function isPendingSendFileMeta(upload: Upload): upload is UploadPendingSendFileMeta {
    return upload.status === 'UploadPendingSendFileMeta'
}

export function isPendingUpload(upload: Upload): upload is UploadPendingRetryUploadFile {
    return upload.status === 'UploadPendingRetryUploadFile'
}

export function isUploadFailed(upload: Upload): upload is UploadFailed {
    return upload.status === 'UploadFailed'
}

export function isUploaded(upload: Upload): upload is Uploaded {
    return upload.status === 'Uploaded'
}

export function isCancelled(upload: Upload): upload is UploadCancelled {
    return upload.status === 'UploadCancelled'
}

export function isInvalid(upload: Upload): upload is UploadInvalid {
    return upload.status === 'UploadInvalid'
}

export function getInvalidUploadIds(uploads: Upload[]): FileId[] {
    return uploads.filter(isInvalid).map((upload) => upload.fileUploadData.fileId)
}

export function isUsingDisk(upload: Upload) {
    // These tasks read and write to disk
    // We don't start reading more data if there's already a lot in progress
    return isWritingFilesToDisk(upload) || isUploadingFile(upload)
}

export function isDone(upload: Upload) {
    return isUploadFailed(upload) || isUploaded(upload) || isCancelled(upload)
}

export function isProcessable(upload: Upload): upload is ProcessableUpload {
    return isPendingSendFileMeta(upload) || isPendingUpload(upload)
}

export function canBeRead(upload: Upload) {
    switch (upload.status) {
        case 'UploadInvalid':
        case 'UploadWritingFileToDisk':
        case 'UploadWriteToDiskFailed':
            return false

        case 'UploadSendFileMetaFailed':
        case 'UploadFailed':
        case 'UploadCancelled':
        case 'Uploaded':
        case 'UploadPendingSendFileMeta':
        case 'UploadSendingFileMeta':
        case 'UploadPendingRetryUploadFile':
        case 'UploadUploadingFile':
            return true

        default:
            assumeExhaustion(upload)
            return true
    }
}

export function shouldProcess(upload: Upload): upload is ProcessableUpload {
    if (!isProcessable(upload)) return false
    if (!upload.nextRetry) return true
    return upload.nextRetry < new Date()
}

export function hasUnsyncedData(upload: Upload) {
    switch (upload.status) {
        case 'UploadInvalid':
        case 'UploadWriteToDiskFailed':
        case 'UploadSendFileMetaFailed':
        case 'UploadFailed':
        case 'UploadCancelled':
        case 'Uploaded':
            return false

        case 'UploadWritingFileToDisk':
        case 'UploadPendingSendFileMeta':
        case 'UploadSendingFileMeta':
        case 'UploadPendingRetryUploadFile':
        case 'UploadUploadingFile':
            return true

        default:
            assumeExhaustion(upload)
            return true
    }
}

export function findUpload(uploads: Upload[], fileId: FileId) {
    return uploads.find((u) => u.fileUploadData.fileId.startsWith(fileId))
}

export function updateUploads(current: Upload[], changed: Upload[]) {
    return pipe(
        current.concat(changed),
        map((u) => u.fileUploadData.fileId),
        uniq,
        map((id) => findUpload(changed, id) ?? findUpload(current, id))
    )
}

export function getFileIds(uploads: Upload[]): FileId[] {
    return uploads.map((f) => f.fileUploadData.fileId)
}
